import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from "@angular/material/card";
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from '@angular/material/input';
import { PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from "@angular/material/select";
import { MatSliderModule } from '@angular/material/slider';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { MatTooltip } from "@angular/material/tooltip";
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { AuthenticationService } from '../../services/authentication.service';
import { BiasingPolicyService } from '../../services/biasing-policy.service';
import { ClientService } from '../../services/client.service';
import { NotificationService } from '../../services/notification.service';
import { BreadcrumbsComponent } from '../../shared/breadcrumbs/breadcrumbs.component';
import { ConfirmModalComponent } from '../../shared/confirm-modal/confirm-modal.component';
import { LoadingComponent } from "../../shared/loading/loading.component";
import { ModalComponent } from '../../shared/modal/modal.component';
import { SharedTableComponent } from "../../shared/shared-table/shared-table.component";

@Component({
  selector: 'app-biasing-policy-detail',
  imports: [
    MatCardModule,
    MatTabsModule,
    SharedTableComponent,
    MatIconModule,
    MatSliderModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    BreadcrumbsComponent,
    LoadingComponent,
    MatTooltip
],
  templateUrl: './biasing-policy-detail.component.html',
  styleUrl: './biasing-policy-detail.component.scss'
})
export class BiasingPolicyDetailComponent implements OnInit, OnDestroy {
  id: string | null = null;
  name: string | null = null;
  sortBy = '';
  sortField = '';
  sortDirection = '';
  searchQuery: string = '';
  totalItems = 0;
  activeTab: string = '';

  isLoading = false;

  // Paginazione client-side della tabella alias
  aliasPageIndex = 0;
  aliasPageSize = 10;
  aliasTotalItems = 0;

  // Paginazione client-side della tabella fields
  fieldPageIndex = 0;
  fieldPageSize = 10;
  fieldTotalItems = 0;

  pagedDataAlias: any[] = [];
  pagedDataField: any[] = [];

  formData!: FormGroup;
  formDataGeneral!: FormGroup;

  decayFactorSlider!: FormControl<number | null>;
  decayFactorInput!: FormControl<number | null>;

  timeValues = [
    { label: 'Giorno', value: 'd', factor: 1 },
    { label: 'Mese', value: 'd', factor: 30 },
    { label: 'Anno', value: 'd', factor: 365 }
  ];

  singleBiasingElement: any = {};

  dataSourceMeta = new MatTableDataSource();
  dataSourceAlias = new MatTableDataSource();

  // destroy$ per chiudere le subscription “infinite”
  private readonly destroy$ = new Subject<void>();

  // configurazione modale a seconda del tab attivo
  tableConfigs: Record<string, {
    visibleFields: string[],
    hiddenFields?: string[],
    readonlyFields?: { edit?: string[], add?: string[], delete?: string[] },
    labels: Record<string, string>,
    hiddenFieldsValues?: Record<string, any>,
    fieldTypes: Record<string, string>,
    selectOptions?: Record<string, { value: any, label: string }[]>,
    loadingSelect?: Record<string, boolean>,
    errorSelect?: Record<string, boolean>
  }> = {
      meta: {
        visibleFields: ['fieldId', 'textRegex', 'boost'],
        readonlyFields: { edit: ['fieldId'] },
        labels: {
          fieldId: 'Field',
          textRegex: 'Testo/Regex',
          boost: 'Boost',
        },
        fieldTypes: {
          fieldId: 'select',
          textRegex: 'input',
          boost: 'slider'
        },
        selectOptions: {
          fieldId: [{ value: '', label: '' }]
        },
        loadingSelect: {
          fieldId: false
        },
        errorSelect: {
          fieldId: false
        }
      },
      alias: {
        visibleFields: ['aliasId', 'boost'],
        readonlyFields: { edit: ['aliasId'] },
        labels: {
          aliasId: 'Alias',
          boost: 'Boost',
        },
        fieldTypes: {
          aliasId: 'select',
          boost: 'slider'
        },
        selectOptions: {
          aliasId: [{ value: '', label: '' }]
        },
        loadingSelect: {
          aliasId: false
        },
        errorSelect: {
          aliasId: false
        }
      }
    };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly biasingService: BiasingPolicyService,
    private readonly clientService: ClientService,
    private readonly dialog: MatDialog,
    private readonly fb: FormBuilder,
    private readonly notificationService: NotificationService,
    private readonly auth: AuthenticationService
  ) { }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: ParamMap) => {
        this.id = params.get('id');
        const encodedName = params.get('name');
        this.name = encodedName ? decodeURIComponent(encodedName) : null;
      });

    const stored = localStorage.getItem('selectedBiasingRow');
    if (stored) {
      this.singleBiasingElement = JSON.parse(stored);
      console.log('dato singolo', this.singleBiasingElement);
    }

    this.activeTab = 'data_general';

    if (this.id) {
      this.loadMetaList();
      this.loadAliasList();
    }

    console.log('tabAttiva', this.activeTab);
    this.decayFactorSlider = new FormControl<number | null>(
      this.singleBiasingElement.decay ?? 0,
      [Validators.required, Validators.min(0), Validators.max(1)]
    );
    this.decayFactorInput = new FormControl<number | null>(
      this.singleBiasingElement.decay ?? 0,
      [Validators.required, Validators.min(0), Validators.max(1)]
    );

    // sincronizzazione bidirezionale tra input e slider
    this.decayFactorInput.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => {
        if (this.decayFactorSlider.value !== val) {
          this.decayFactorSlider.setValue(val, { emitEvent: false });
        }
      });

    this.decayFactorSlider.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => {
        if (this.decayFactorInput.value !== val) {
          this.decayFactorInput.setValue(val, { emitEvent: false });
        }
      });

    this.formData = this.fb.group({
      decay: this.decayFactorInput,
      offset: this.fb.group({
        number: [1],
        timeValue: ['giorno']
      }),
      scale: this.fb.group({
        number: [1],
        timeValue: ['giorno']
      })
    });

    const offsetStringFromBE = this.singleBiasingElement.offset;
    const scaleStringFromBE = this.singleBiasingElement.scale;

    const offsetParsed = this.parseFromDaysString(offsetStringFromBE);
    const scaleParsed = this.parseFromDaysString(scaleStringFromBE);

    this.formData.get('offset')?.patchValue(offsetParsed);
    this.formData.get('scale')?.patchValue(scaleParsed);

    this.formDataGeneral = this.fb.group({
      name: this.singleBiasingElement.name,
      description: [this.singleBiasingElement.description, [Validators.required]]
    });
  }

  getOffsetString() {
    const val = this.formData.get('offset')?.value;
    return this.convertToDays(val.number, val.timeValue);
  }

  getScaleString() {
    const val = this.formData.get('scale')?.value;
    return this.convertToDays(val.number, val.timeValue);
  }

  convertToDays(number: number, timeValue: string) {
    switch (timeValue) {
      case 'giorno':
        return `${number}d`;
      case 'mese':
        return `${number * 30}d`;
      case 'anno':
        return `${number * 365}d`;
      default:
        return `${number}d`;
    }
  }

  // Accetta anche undefined/null, gestito con default
  parseFromDaysString(ndString?: string | null): { number: number, timeValue: string } {
    if (!ndString) {
      return { number: 1, timeValue: 'giorno' };
    }
    const numericPart = ndString.replaceAll(/d/gi, '').trim();
    const days = Number.parseInt(numericPart, 10);

    if (Number.isNaN(days)) {
      return { number: 1, timeValue: 'giorno' };
    }

    if (days % 365 === 0) {
      return { number: days / 365, timeValue: 'anno' };
    } else if (days % 30 === 0) {
      return { number: days / 30, timeValue: 'mese' };
    } else {
      return { number: days, timeValue: 'giorno' };
    }
  }

  onSubmit() {
    if (this.formData.valid) {
      const payload = {
        ...this.formData.value,
        offset: this.getOffsetString(),
        scale: this.getScaleString(),
        id: this.id,
        description: this.singleBiasingElement.description,
        name: this.singleBiasingElement.name
      };
      console.log('payload: ', payload);
      this.isLoading = true;

      this.biasingService.editBiasingPolicy(payload).subscribe({
        next: (response) => {
          localStorage.setItem('selectedBiasingRow', JSON.stringify(response));
          console.log('response', response);
          this.isLoading = false;
          this.notificationService.successOnSave();
        },
        error: (err) => {
          console.log("errore nella richiesta", err);
          this.isLoading = false;
          this.notificationService.genericError();
        }
      });

    } else {
      this.formData.markAllAsTouched();
    }
  }

  onSubmitGeneralData() {
    if (this.formDataGeneral.valid) {
      const payload = {
        ...this.formDataGeneral.value,
        offset: this.singleBiasingElement.offset,
        scale: this.singleBiasingElement.scale,
        decay: this.singleBiasingElement.decay,
        id: this.id
      };
      console.log('payload completo: ', payload);
      this.isLoading = true;

      this.biasingService.editBiasingPolicy(payload).subscribe({
        next: (response) => {
          localStorage.setItem('selectedBiasingRow', JSON.stringify(response));
          console.log('response', response);
          this.isLoading = false;
          this.notificationService.successOnSave();
        },
        error: (err) => {
          console.log("errore nella richiesta", err);
          this.isLoading = false;
          this.notificationService.genericError();
        }
      });
    } else {
      this.formDataGeneral.markAllAsTouched();
    }
  }

  // è la lista di meta associati alla biasing policy - non la lista dei meta creati
  loadMetaList() {
    this.dataSourceMeta.data = this.singleBiasingElement.biasingPolicyFields ?? [];
    this.fieldTotalItems = this.singleBiasingElement.biasingPolicyFields?.length ?? 0;
    console.log('meta list data', this.dataSourceMeta.data);
  }

  // è la lista di alias associati alla biasing policy - non la lista degli alias creati
  loadAliasList() {
    this.dataSourceAlias.data = this.singleBiasingElement.biasingPolicyAliases ?? [];
    this.aliasTotalItems = this.singleBiasingElement.biasingPolicyAliases?.length ?? 0;
    console.log('alias list data', this.dataSourceAlias.data);
  }

  onTabChanged(event: MatTabChangeEvent) {
    switch (event.index) {
      case 0:
        this.activeTab = 'data_general';
        break;
      case 1:
        this.activeTab = 'meta';
        break;
      case 2:
        this.activeTab = 'peso_data';
        break;
      case 3:
        this.activeTab = 'alias';
        break;
      default:
        this.activeTab = '';
    }
  }

  onSortChanged(sort: Sort) {
    console.log('sort', sort);
  }

  // Cambio pagina / page size (client-side)
  onPageChanged(ev: PageEvent) {
    if (this.activeTab === 'alias') {
      this.aliasPageIndex = ev.pageIndex;
      this.aliasPageSize = ev.pageSize;
    }
    if (this.activeTab === 'meta') {
      this.fieldPageIndex = ev.pageIndex;
      this.fieldPageSize = ev.pageSize;
    }
    this.updatePage();
  }

  private updatePage() {
    if (this.activeTab === 'alias') {
      const start = this.aliasPageIndex * this.aliasPageSize;
      const end = start + this.aliasPageSize;
      this.pagedDataAlias = this.dataSourceAlias.data.slice(start, end);
    }
    if (this.activeTab === 'meta') {
      const start = this.fieldPageIndex * this.fieldPageSize;
      const end = start + this.fieldPageSize;
      this.pagedDataField = this.dataSourceMeta.data.slice(start, end);
    }
  }

  getSelectOptionsForModal(tab: string, modalIInstance?: any, action?: string) {
    const config = this.tableConfigs[tab];
    if (tab === 'meta') {
      config.loadingSelect!['fieldId'] = true;
      config.errorSelect!['fieldId'] = false;

      this.clientService.getAllFields()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            const metaOptions = response as Array<{ value: string; label: string; system: boolean }>;
            if (config.selectOptions) {
              config.selectOptions['fieldId'] = metaOptions
                .filter((x: any) => x.system === false)
                .map((x: any) => ({ value: x.id, label: x.name }));
            }
            config.loadingSelect!['fieldId'] = false;

            if(modalIInstance?.formData){
              const isReadonlyInEdit = config.readonlyFields?.edit?.includes('fieldId') && action === 'edit';

              if(!isReadonlyInEdit){
                modalIInstance.formData.get('fieldId')?.enable();
              }
            }
          },
          error: () => {
            config.selectOptions!['fieldId'] = [];
            config.loadingSelect!['fieldId'] = false;
            config.errorSelect!['fieldId'] = true;
          } 
          
        });
    }
    if (tab === 'alias') {
      config.loadingSelect!['aliasId'] = true;
      config.errorSelect!['aliasId'] = false;

      this.clientService.getAllAlias()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            const metaOptions = response as Array<{ value: string; label: string }>;
            if (config.selectOptions) {
              config.selectOptions['aliasId'] = metaOptions
                .map((x: any) => ({ value: x.id, label: x.name }));
            }
            config.loadingSelect!['aliasId'] = false;

            if(modalIInstance?.formData){
              const isReadonlyInEdit = config.readonlyFields?.edit?.includes('aliasId') && action === 'edit';

              if(!isReadonlyInEdit){
                modalIInstance.formData.get('aliasId')?.enable();
              }
            }
          },
          error: () => {
            config.selectOptions!['aliasId'] = [];
            config.loadingSelect!['aliasId'] = false;
            config.errorSelect!['aliasId'] = true;
          } 
          
        });
    }
  }

  openDialog(event: { data: any, type: 'add' | 'edit' | 'delete' }) {
    const activatedTab = this.activeTab;
    console.log('tab attivo', activatedTab);
    const config = this.tableConfigs[activatedTab];


    const dialogRef = this.dialog.open(ModalComponent, {
      panelClass: 'no-scroll-policy-dialog',
      width: '400px',
      data: {
        action: event.type,
        payload: event.data,
        visibleFields: config.visibleFields,
        readonlyFields: config.readonlyFields,
        hiddenFields: config.hiddenFields,
        hiddenFieldsValues: config.hiddenFieldsValues,
        labels: config.labels,
        fieldTypes: config.fieldTypes,
        selectOptions: config.selectOptions,
        loadingSelect: config.loadingSelect,
        errorSelect: config.errorSelect
      }
    });

    this.getSelectOptionsForModal(activatedTab, dialogRef.componentInstance, event.type);

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        console.log('result', result);
        if (result) {
          this.isLoading = true;

          // caso meta
          if (activatedTab === 'meta') {
            if (event.type === 'add') {
              const index = this.dataSourceMeta.data.findIndex((el: any) => el.fieldId === result.fieldId);
              if (index !== -1) {
                this.isLoading = false;
                this.dialog.open(ConfirmModalComponent, {
                  width: '400px',
                  data: { message: 'Un field con lo stesso ID è già stato associato.', title: 'Attenzione' }
                });
                return;
              }
              const payload = { ...result };
              const biasingId = this.id!;
              this.biasingService.addOrEditFieldToBiasing(payload, biasingId).pipe(finalize(() => { this.isLoading = false; })).subscribe({
                next: (response) => {
                  this.singleBiasingElement.biasingPolicyFields = response.biasingPolicyFields;
                  localStorage.setItem('selectedBiasingRow', JSON.stringify(response));
                  this.loadMetaList();
                  this.notificationService.entitySuccess('add', 'Field');
                },
                error: (err) => {
                  console.log("errore nella richiesta", err);
                  this.notificationService.entityError('add', 'Field');
                }
              });
            } else if (event.type === 'edit') {
              const index = this.dataSourceMeta.data.findIndex((el: any) => el.fieldId === result.fieldId);
              const payload = { ...result };
              const biasingId = this.id!;
              if (index !== -1) {
                this.biasingService.addOrEditFieldToBiasing(payload, biasingId).pipe(finalize(() => { this.isLoading = false; })).subscribe({
                  next: (response) => {
                    this.singleBiasingElement.biasingPolicyFields = response.biasingPolicyFields;
                    localStorage.setItem('selectedBiasingRow', JSON.stringify(response));
                    this.loadMetaList();
                    this.notificationService.entitySuccess('update', 'Field');
                  },
                  error: (err) => {
                    console.log("errore nella richiesta", err);
                    this.notificationService.entityError('update', 'Field');
                  }
                });
              }
            } else if (event.type === 'delete') {
              this.biasingService.removeFieldFromBiasing(result.biasingPolicyId, result.fieldId).pipe(finalize(() => { this.isLoading = false; })).subscribe({
                next: (response) => {
                  this.singleBiasingElement.biasingPolicyFields = response.biasingPolicyFields;
                  localStorage.setItem('selectedBiasingRow', JSON.stringify(response));
                  this.loadMetaList();
                  this.notificationService.entitySuccess('delete', 'Field');
                },
                error: (err) => {
                  console.log("errore nella richiesta", err);
                  this.notificationService.entityError('delete', 'Field');
                }
              });
            }
          }

          // caso alias
          if (activatedTab === 'alias') {
            if (event.type === 'add') {
              const index = this.dataSourceAlias.data.findIndex((el: any) => el.aliasId === result.aliasId);
              if (index !== -1) {
                this.isLoading = false;

                this.dialog.open(ConfirmModalComponent, {
                  width: '400px',
                  data: { message: 'Un alias con lo stesso ID è già stato associato.', title: 'Attenzione' }
                });
                return;
              }
              const payload = { ...result };
              const biasingId = this.id!;
              this.biasingService.addOrEditAliasToBiasing(payload, biasingId).pipe(finalize(() => { this.isLoading = false; })).subscribe({
                next: (response) => {
                  this.singleBiasingElement.biasingPolicyAliases = response.biasingPolicyAliases;
                  localStorage.setItem('selectedBiasingRow', JSON.stringify(response));
                  this.loadAliasList();
                  this.notificationService.entitySuccess('add', 'Alias');
                },
                error: (err) => {
                  console.log("errore nella richiesta", err);
                  this.notificationService.entityError('add', 'Alias');
                }
              });
            } else if (event.type === 'edit') {
              const index = this.dataSourceAlias.data.findIndex((el: any) => el.aliasId === result.aliasId);
              const payload = { ...result };
              const biasingId = this.id!;
              if (index !== -1) {
                this.biasingService.addOrEditAliasToBiasing(payload, biasingId).pipe(finalize(() => { this.isLoading = false; })).subscribe({
                  next: (response) => {
                    this.singleBiasingElement.biasingPolicyAliases = response.biasingPolicyAliases;
                    localStorage.setItem('selectedBiasingRow', JSON.stringify(response));
                    this.loadAliasList();
                    this.notificationService.entitySuccess('update', 'Alias');
                  },
                  error: (err) => {
                    console.log("errore nella richiesta", err);
                    this.notificationService.entityError('update', 'Alias');
                  }
                });
              }
            } else if (event.type === 'delete') {
              this.biasingService.removeAliasFromBiasing(result.biasingPolicyId, result.aliasId).pipe(finalize(() => { this.isLoading = false; })).subscribe({
                next: (response) => {
                  this.singleBiasingElement.biasingPolicyAliases = response.biasingPolicyAliases;
                  localStorage.setItem('selectedBiasingRow', JSON.stringify(response));
                  this.loadAliasList();
                  this.notificationService.entitySuccess('delete', 'Alias');
                },
                error: (err) => {
                  console.log("errore nella richiesta", err);
                  this.notificationService.entityError('delete', 'Alias');
                }
              });
            }
          }
        }
      });
  }

  canDoActions(role: string): boolean {
    return this.auth.hasRole(role);
  }

  ngOnDestroy(): void {
    // chiudo tutte le subscription
    this.destroy$.next();
    this.destroy$.complete();

    localStorage.removeItem('selectedBiasingRow');
  }
}